import type { Step as RcStep } from '@sb/remote-control/types';
import type { Space, StepKind } from '@sb/routine-runner';

import { type ValidationContext, type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class MoveArmToRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.MoveArmTo', 'MoveArmTo');
  }

  protected validate({
    parents,
    args,
    routine,
    ctx,
  }: CanonicalValidateArgs): void {
    type MoveArmToArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'MoveArmTo' }
    >;
    const a = args as MoveArmToArgs;
    const spaceItems = this.getSpaceItems(routine, ctx);
    this.checkTargetPresence(a);
    this.checkPositionList(a, spaceItems);
    this.checkAnchoredVisionMatchJointAngles(a, spaceItems);
    this.checkExpression(a);
    this.checkAddOffsetConstraint(parents, a);
    this.checkCustomBaseMatchJointAngles(a, ctx);
    this.checkPushModeConstraint(parents, a);
  }

  /**
   * Ensure a target pose or jointAngles is set when required.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkTargetPresence(args: RcStep.Arguments): void {
    const a = args as Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>;
    const t = a.target;
    const hasTarget = !!(t?.jointAngles || t?.pose);

    if (a.targetKind === 'singlePosition' && !hasTarget) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Joint angles or pose must be set',
        fieldId: 'jointAngles',
      });
    }
  }

  private checkPositionList(
    args: RcStep.Arguments,
    spaceItems: Space.Item[],
  ): void {
    /**
     * Validate position list selection when targetKind=positionList.
     * Throws ActionRequiredError if list missing or invalid.
     *
     * @throws {ActionRequiredError}
     */
    const a2 = args as Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>;
    if (a2.targetKind !== 'positionList') return;

    if (!a2.positionListID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list must be set',
        fieldId: 'positionListID',
      });
    }

    if (!spaceItems.some((s) => s.id === a2.positionListID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Set the Position list linked to Move Arm step1.',
      });
    }
  }

  /**
   * Block `shouldMatchJointAngles` when the referenced position list is an
   * anchored item (`anchoredToID != null`) or a vision position list (mirrors
   * the Waypoint rule). The legacy v1 editor can still create these steps, and
   * the server-side hard throw that used to guard this was removed, so this
   * run-time check blocks running and surfaces a per-step error the operator
   * can fix in place.
   *
   * @throws {ActionRequiredError}
   */
  private checkAnchoredVisionMatchJointAngles(
    args: Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>,
    spaceItems: Space.Item[],
  ): void {
    if (args.targetKind !== 'positionList' || !args.positionListID) return;
    if (!(args.shouldMatchJointAngles ?? false)) return;

    const spaceItem = spaceItems.find((s) => s.id === args.positionListID);
    if (!spaceItem) return;

    const isAnchored = spaceItem.anchoredToID != null;
    const isVision = spaceItem.kind === 'visionPositionList';

    if (isAnchored || isVision) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Match joint angles is not allowed for anchored or vision positions. ' +
          'Turn off "Match joint angles" to run this routine.',
        fieldId: 'shouldMatchJointAngles',
      });
    }
  }

  private checkExpression(args: RcStep.Arguments): void {
    /**
     * Validate expression target when targetKind=expression.
     * Throws ActionRequiredError if expression invalid or deprecated.
     *
     * @throws {ActionRequiredError}
     */
    const a3 = args as Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>;
    if (a3.targetKind !== 'expression' || a3.expression == null) return;

    validateCodeExpression(a3.expression, 'expression', 'Position expression');
  }

  private checkAddOffsetConstraint(
    parents: Array<{ stepKind: StepKind | string }>,
    args: RcStep.Arguments,
  ): void {
    /**
     * Enforce constraint for MoveArm inside AddOffset.
     * Throws ActionRequiredError when shouldMatchJointAngles is true.
     *
     * @throws {ActionRequiredError}
     */
    const insideAddOffset = parents.some((p) => p?.stepKind === 'AddOffset');

    const a4 = args as Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>;

    if (insideAddOffset && ((a4.shouldMatchJointAngles ?? false) as boolean)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Move Arm steps inside an Add Offset step may not require exact final joint position, as only the tooltip pose can be guaranteed.',
        fieldId: 'shouldMatchJointAngles',
      });
    }
  }

  /**
   * Block shouldMatchJointAngles when a custom base is enabled.
   *
   * @throws {ActionRequiredError}
   */
  private checkCustomBaseMatchJointAngles(
    args: Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>,
    ctx: ValidationContext,
  ): void {
    if (ctx.featureFlags?.allowCustomBaseMatchJointAngles) return;
    if (!(args.shouldMatchJointAngles ?? false)) return;

    const hasCustomBase = (ctx.equipment ?? []).some(
      (item) => item.config.kind === 'CustomBase' && item.isEnabled,
    );

    if (!hasCustomBase) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: 'Match Joint angles may not be used with a custom base',
      fieldId: 'shouldMatchJointAngles',
    });
  }

  private checkPushModeConstraint(
    parents: Array<{ stepKind: StepKind | string }>,
    args: RcStep.Arguments,
  ): void {
    /**
     * Enforce linear motion for PushMode moves.
     * Throws ActionRequiredError when motionKind !== 'line'.
     *
     * @throws {ActionRequiredError}
     */
    const isPushModeMove = parents.some((p) => p?.stepKind === 'PushMode');

    const a5 = args as Extract<RcStep.Arguments, { argumentKind: 'MoveArmTo' }>;

    if (isPushModeMove && a5.motionKind !== 'line') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          "Push mode move steps must be linear.  Set 'Move in a straight line' option to true",
      });
    }
  }
}

export const MoveArmToValidation: ValidationRule =
  new MoveArmToRule().toValidationRule();
